import React, { useEffect, useState } from "react";
import { AdminApprovalGroup, AdminUser, IActionHandler, OrgInfo, Sort, SubsumeRequest, UserProfile } from "@preveil-api";
import {
  useAppDispatch, useGetOrgInfo, Account, AdminErrorMessages, Message, MessageHandlerDisplayType, sortBy, SORT_DIRECTION,
  OrganizationInformation, getOppositeSortDirection, GlobalErrorMessages, MessageAnchors, useAppSelector, AdminSuccessMessages,
  MessageToastTypes, useDeleteOrgUserMutation, UserRequest, RequestTypes, OrgUserStatus, useGetUsersOrgsByEntityIdQuery, AdminWarningMessages,
  useDeleteOrgUserInviteMutation,
  RequestTypesType
} from "src/common";
import { CoverTemplate, PageHeader } from "src/components";
import { SidePanel, UsersListView, UsersToolbar } from ".";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  account: Account;
  org_info: OrgInfo
}

function AdminUsersComponent(props: AllProps) {
  const { account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [filter_param, setFilterParam] = useState<string>("");
  const [users_list, setUsersList] = useState<AdminUser[]>([]);
  const [filtered_list, setFilteredList] = useState<AdminUser[]>([]);
  const [admin_users_count, setAdminUsersCount] = useState<number>(0);
  const [allow_add_devices, setAllowAddDevices] = useState<boolean>();
  const [add_device_overrides, setAddDeviceOverrides] = useState<string[]>([]);
  const [current_selected_user, setCurrentSelectedUser] = useState<AdminUser>();
  const [recovery_groups, setRecoveryGroups] = useState<AdminApprovalGroup[]>([]);
  const [admin_approval_group, setAdminApprovalGroup] = useState<AdminApprovalGroup>();
  const [sort, setSort] = useState<Sort<keyof AdminUser>>({ field: "user_id", direction: "asc" });
  // hooks
  const { data, error, refresh } = useGetOrgInfo(account, org_info);
  const [deleteOrgUser] = useDeleteOrgUserMutation();
  const [deleteOrgUserInvite] = useDeleteOrgUserInviteMutation();
  const { data: org_data } = useGetUsersOrgsByEntityIdQuery({
    account_ids: Account.getAccountIdentifiers(account),
    body: { entity_id: org_info.org_id },
  });

  // Description: On initial load - call the use get org info hook to get the approval groups and requests.
  useEffect(() => {
    fetchOrgData();
    return () => {
      dispatch(adminActions.setSelected([]));
    };
  }, []);

  // Description: set the add device overrides from query result.
  useEffect(() => {
    if (!_.isEmpty(org_data) && !!org_data.add_device_overrides && !org_data.allow_add_devices) {
      const { add_device_overrides } = org_data;
      setAddDeviceOverrides(add_device_overrides);
    }
  }, [org_data]);

  // Description: handle data and error from getOrgInfo hook.
  useEffect(() => {
    !!data && data?.org_users && setOrgData(data);

    if (!!error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_fetching_org_info, MessageHandlerDisplayType.toastr), error));
      setIsLoading(false);
    }
  }, [data, error]);

  // Description: Fetch the org data.
  function fetchOrgData() {
    setIsLoading(true);
    return refresh({
      history: false,
      status: "pending",
      hideExpired: true,
      limit: 200,
    });
  };

  // Description: Parses the data from getOrgInfo hook and sets various states.
  function setOrgData(data: OrganizationInformation) {
    let total_admin_users = 0;
    const { org_users, approval_groups, org_requests, org_info } = data;
    const { roled_approval_groups } = org_info;
    if (!!roled_approval_groups && !!roled_approval_groups.admin_approval_group) {
      const approval_group = approval_groups.find((group) => group.approval_group_id === roled_approval_groups.admin_approval_group?.group_id);
      !!approval_group && setAdminApprovalGroup(approval_group);
    }
    setAllowAddDevices(org_info.allow_add_devices);
    const { pending_group_updates, invited_users } = parseOrgRequests(org_requests);
    const users = org_users.map((user) => {
      const { account_type, display_name, user_id, entity_metadata, devices, claimed, account_version, recovery_group } = user;
      const { department, role } = entity_metadata;
      const status = !!claimed ? "active" : "pending"; // Claimed is a boolean value so we need to add a label to it.
      const updates_pending = pending_group_updates[user_id] ? pending_group_updates[user_id].length : 0; // Recovery group updates pending for this specific user.
      const user_pending_delete_total = updates_pending > 0 && pending_group_updates[user_id] ? pending_group_updates[user_id].filter((request) => request.request_type === RequestTypes.delete_user).length : undefined;
      const user_pending_role_change_total = updates_pending > 0 && pending_group_updates[user_id] ? pending_group_updates[user_id].filter((request) => request.request_type === RequestTypes.change_admin_status).length : undefined;
      const user_pending_group_change_total = updates_pending > 0 && pending_group_updates[user_id] ? pending_group_updates[user_id].filter((request) => request.request_type === RequestTypes.member_rekey_and_set_approval_group).length : undefined;
      const org_user = {
        account_type,
        account_version,
        user_id,
        display_name,
        status,
        role,
        department,
        recovery_group,
        devices,
        updates_pending,
        user_pending_delete_total,
        user_pending_role_change_total,
        user_pending_group_change_total
      };
      // we want to keep count of how many admins are within the org.
      // if there is only one admin in the org, we need to disable the change role button.
      total_admin_users = role === "admin" ? total_admin_users + 1 : total_admin_users;
      return org_user;
    });
    const users_list = [...users, ...invited_users];
    const sorted_users_list = sortBy(users_list, { field: "user_id", direction: SORT_DIRECTION.ascending });
    setAdminUsersCount(total_admin_users);
    setUsersList(sorted_users_list);
    setFilteredList(sorted_users_list);
    setRecoveryGroups(approval_groups.filter((group) => !group.is_deleted));
    setIsLoading(false);
  }

  // Description: parses through the org requests
  // and returns the pending group updates for each user if there is any.
  // and the subsume requests.
  function parseOrgRequests(requests: UserRequest[]): { pending_group_updates: { [key: string]: Array<{ request_id: string, request_type: RequestTypesType }> }, invited_users: AdminUser[] } {
    const pending_group_updates: { [key: string]: Array<{ request_id: string, request_type: RequestTypesType }> } = {};
    const invited_users: AdminUser[] = [];
    if (requests.length >= 1) {
      requests.forEach((request: UserRequest) => {
        const { type } = request;
        if (type === RequestTypes.member_rekey_and_set_approval_group || type === RequestTypes.delete_user || type === RequestTypes.change_admin_status) {
          const { for_users } = request; // each request can have multiple users.
          for_users.forEach((user: UserProfile) => {
            const { address } = user;
            if (!pending_group_updates[address]) {
              pending_group_updates[address] = [{ request_id: request.request_id, request_type: type }];
            } else if (pending_group_updates[address]) {
              pending_group_updates[address].push({ request_id: request.request_id, request_type: type });
            }
          });
        } else if (type === RequestTypes.subsume_account) {
          // Iterate over the subsume request users since those are not part of the org users (not present in the org_users array).
          const { for_users, is_admin, metadata, request_id } = request;
          const { department } = metadata as SubsumeRequest;
          const role = is_admin ? "admin" : "standard";
          const status = request.status === OrgUserStatus.pending ? OrgUserStatus.invited : request.status;
          const display_name = for_users[0].name as string;
          const user_id = for_users[0].address;

          invited_users.push({ status, display_name, user_id, department, role, devices: [], subsume_request_id: request_id });
        }
      });
    }
    return { pending_group_updates, invited_users };
  }

  // Description: Handles the request to remove selected users from the organization.
  async function removeSelectedUsersFromOrg(users: AdminUser[]) {
    if (_.isArray(users)) {
      const params = {
        user_id: account.user_id,
        org_id: org_info.org_id,
      };
      const failed_users_request = [];
      let invited_users: number = 0;
      // If there are multiple users selected, we need to make a request for each user.
      let message = "";
      await Promise.all(users.map(async (user: AdminUser) => {
        const active_ag_member = recovery_groups.some((group) => group.approvers.some((member) => member.address === user.user_id));
        if (active_ag_member) {
          message = AdminErrorMessages.error_request_delete_active_AG_member;
          failed_users_request.push(user.user_id);
        } else {
          if (user.status === OrgUserStatus.invited && !!user.subsume_request_id) {
            await deleteOrgUserInvite({ ...params, request_id: user.subsume_request_id }).unwrap()
              .then(() => {
                invited_users++;
                setUsersList(users_list.filter((org_user) => org_user.user_id !== user.user_id));
              }).catch(() => {
                failed_users_request.push(user.user_id);
              });
          } else {
            await deleteOrgUser({ ...params, user_to_be_deleted_id: user.user_id }).unwrap()
              .then(() => {
                setUsersList(users_list.filter((org_user) => org_user.user_id !== user.user_id));
              }).catch(() => {
                failed_users_request.push(user.user_id);
              });
          }
        }
      }));

      const success_deleted = users.length - failed_users_request.length;
      if (success_deleted > 0) {
        if (success_deleted === invited_users) {
          const message = AdminSuccessMessages.success_request_delete_invited_users.replace(MessageAnchors.message_content, `${invited_users} user invite${invited_users > 1 ? "s" : ""}`);
          dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.success)));
        } else if (admin_approval_group) {
          dispatch(uiActions.handleSetMessage(new Message(AdminWarningMessages.warning_approval_delete_user.replace(MessageAnchors.prev_name, admin_approval_group.name).replace(MessageAnchors.message_content, `${success_deleted - invited_users} user${(success_deleted - invited_users) > 1 ? "s" : ""}`), MessageHandlerDisplayType.warning, MessageToastTypes.warning)));
        } else {
          const users_message_template = success_deleted === users.length ? `${users.length} users` : `${success_deleted} out of ${users.length} users`;
          const successMessage = AdminSuccessMessages.success_request_delete_users.replace(MessageAnchors.message_content, `${users_message_template}`);
          const display_type = success_deleted === users.length ? MessageToastTypes.success : MessageToastTypes.warning;
          dispatch(uiActions.handleSetMessage(new Message(successMessage, MessageHandlerDisplayType.toastr, display_type)));
        }
      } else {
        message = message || AdminErrorMessages.error_request_delete_user.replace(MessageAnchors.message_content, `${users.length} user${users.length > 1 ? "s" : ""} from organization.`);
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr)));
      }
      dispatch(adminActions.setSelected([]));
      fetchOrgData();
    }
  }

  const AdminUsersRequests = {
    handleSetSort: (params: "display_name" | "user_id" | "department" | "recovery_group" | "role") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = sortBy(filtered_list, { field: params, direction });
      setSort({ field: params, direction });
      setFilteredList(sorted_requests);
    },
    handleGetRefreshRequest: () => {
      fetchOrgData();
    },
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = users_list.filter((user: AdminUser) => {
          return Object.values(user).some(field =>
            _.isString(field) && field.toLowerCase().includes(param.toLowerCase())
          );
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(users_list);
      }
    },
    handleShowSideNavMenu: (params?: { user: AdminUser }) => {
      const user = !!params ? params.user : undefined;
      setCurrentSelectedUser(user);
    },
    handleDelete: () => {
      const users_to_delete = users_list.filter((user) => selected.includes(user.user_id));
      removeSelectedUsersFromOrg(users_to_delete);
    },
    handleSelect: (params: { user?: AdminUser, selected: boolean }) => {
      let new_selected = selected.slice();
      const _id = !!params.user ? params.user.user_id : null;
      if (!!_id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
            new_selected.push(_id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        // NOTE: Filter out locked files as those entries should not be selected, deleted, etc when they are locked.
        new_selected = params.selected ? users_list.map((user) => user.user_id) : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
    }
  };

  function handlePageActions(actionObj: IActionHandler) {
    const callback = `handle${actionObj.actionType}`;
    if ((AdminUsersRequests as any)[callback] instanceof Function) {
      (AdminUsersRequests as any)[callback](actionObj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, actionObj.actionType);
      AdminUsersRequests.handlePageErrorMessage({ message, stack: actionObj });
    }
  }

  return <CoverTemplate className="admin-component">
    <PageHeader>
      <h1>Users</h1>
      <UsersToolbar current_account={account} is_loading={is_loading} users_list={users_list} handleAction={handlePageActions} filter_param={filter_param} recovery_groups={recovery_groups} />
    </PageHeader>
    <div id="outer-scope" className="cover-content">
      <UsersListView sort={sort} users_list={filtered_list} is_loading={is_loading} selected={selected} handleAction={handlePageActions} />
    </div>
    {!!current_selected_user && (
      <SidePanel
        handleAction={handlePageActions}
        current_account={account}
        current_selected_user={current_selected_user}
        allow_add_devices={allow_add_devices}
        add_device_overrides={add_device_overrides}
        recovery_groups={recovery_groups}
        total_admin_users={admin_users_count}
        users_list={users_list}
        admin_approval_group={admin_approval_group}
      ></SidePanel>
    )}
  </CoverTemplate>;
}

export default React.memo(AdminUsersComponent);
